import { Injectable } from '@angular/core';
import { Action } from '@ngrx/store';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { EMPTY, Observable, of, timer } from 'rxjs';
import { catchError, filter, first, map, switchMap, takeUntil, tap } from 'rxjs/operators';

import * as fromActions from './actions';
import { SESSION_IDLE_MONITOR_ROLES } from 'core/consts/session-idle-monitor-roles';
import { ApiTokenService } from '../../services/api/api.token.service';
import { ApiTwoFactorService } from '../../services/api/api.two-factor.service';
import { ApiUserService } from '../../services/api/api.user.service';
import { ApiUserSessionService } from '../../services/api/api.user-session.service';
import { RoleStorageService } from '../../services/role.storage.service';
import { SessionIdleMonitorService } from '../../services/session-idle-monitor.service';
import { TokenStorageService } from '../../services/token.storage.service';
import { TwoFactorStorageService } from '../../services/two-factor.storage.service';
import { UserCredentialsStorageService } from '../../services/user-credentials.storage.service';
import { Router } from '@angular/router';
import { ModuleEnum, TwoFactorType, UserRoleEnum } from 'core/enums';
import {
    ExpiredPasswordLoginError,
    TokenModel,
    TokenResponse,
    TwoFactorChallenge,
    TwoFactorEmailStartResponse,
    TwoFactorErrorCode,
    TwoFactorFinalizePayload,
    TwoFactorTranslationKey,
} from 'core/models';
import { RecipientOrganizationModel } from 'core/models/recipient.model';
import {
    getTwoFactorExpiryDelayMs,
    isTwoFactorChallengeExpired,
} from 'core/utils/two-factor-expiry';
import { MatDialog } from '@angular/material/dialog';
import { ChangeRecipientPopupComponent } from 'src/app/theme/navbar/change-recipient-popup/change-recipient-popup.component';
import { ChangeRecipientPopupData } from 'src/app/theme/navbar/change-recipient-popup/change-recipient-popup.interface';
import { MultisessionConflictDialogComponent } from 'src/app/auth/multisession-confilct-dialog/multisession-conflict-dialog.component';

interface AuthTokenModel extends TokenModel {
    user: TokenModel['user'] & {
        Roles?: string;
        Recipients?: RecipientOrganizationModel[];
        Organization?: {
            Id?: string;
            Name?: string;
        };
        Id?: string;
        SpecialRights?: string;
        Modules?: ModuleEnum[];
    };
}

interface LoginErrorTokenContainer {
    token?: string;
    Token?: string;
}

interface LoginErrorBody extends ExpiredPasswordLoginError {
    data?: LoginErrorTokenContainer;
    Data?: LoginErrorTokenContainer;
    details?: LoginErrorTokenContainer;
    Details?: LoginErrorTokenContainer;
    Roles?: UserRoleEnum[];
    roles?: UserRoleEnum[];
    UserRoles?: UserRoleEnum[];
    userRoles?: UserRoleEnum[];
    type?: string | number;
    pendingToken?: string;
    maskedEmail?: string;
    expiresIn?: number | string;
    pendingTokenExpiresIn?: number | string;
}

@Injectable()
export class AuthEffects {
    private readonly multisessionRestrictedRoles = [
        UserRoleEnum.AdminSubRecipient,
        UserRoleEnum.AdminSubRecipientPlus,
    ];
    private readonly necessaryPasswordResetCode = 'NECESSARY_PASSWORD_RESET';
    private readonly twoFactorResendCooldownMs = 60000;
    private readonly twoFactorPendingTokenTtlMs = 5 * 60 * 1000;
    private readonly expiredPasswordIndicators = [
        'passwordexpired',
        'expiredpassword',
        'passwordhasexpired',
        'currentpasswordexpired',
        'passwordexpiration',
        'changepasswordrequired',
        'passwordchangerequired',
        'requirechangepassword',
        'passwordmustbechanged',
        'mustchangepassword',
        'needchangepassword',
        'forcechangepassword',
        'forcepasswordchange',
        'passwordrenewrequired',
        'passwordupdaterequired',
    ];

    public checkAuth$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.AuthActionTypes.CHECK_AUTH),
            switchMap(() =>
                this.apiUserService.userInfo().pipe(
                    map(user => {
                        return new fromActions.LoginUserSuccess(user);
                    }),
                    catchError(error => of(new fromActions.CheckAuthError(error))),
                ),
            ),
        ),
    );
    public loginUser$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.AuthActionTypes.LOGIN_USER),
            map((action: fromActions.LoginUser) => action.payload),
            switchMap(payload =>
                this.apiTokenService
                    .token(payload.email, payload.password, !!payload.terminatePreviousSession)
                    .pipe(
                        switchMap(token => {
                            const challenge = this.getTwoFactorChallenge(token);

                            if (challenge) {
                                return this.activateTwoFactorChallenge(challenge);
                            }

                            return this.completeLogin(token as AuthTokenModel);
                        }),
                        catchError(error => {
                            if (
                                this.getExpiredPasswordToken(error) &&
                                this.isExpiredPasswordError(error)
                            ) {
                                return this.handleLoginError(payload, error);
                            }

                            const challenge = this.getTwoFactorChallenge(error);

                            if (challenge) {
                                return this.activateTwoFactorChallenge(challenge);
                            }

                            return this.handleLoginError(payload, error);
                        }),
                    ),
            ),
        ),
    );
    public requestTwoFactorEmailCode$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.AuthActionTypes.LOGIN_USER_TWO_FACTOR_REQUIRED),
            filter(
                (action: fromActions.LoginUserTwoFactorRequired) =>
                    action.payload.type === TwoFactorType.EmailCode,
            ),
            map(action => new fromActions.StartTwoFactorEmailCode(action.payload.pendingToken)),
        ),
    );
    public startTwoFactorEmailCode$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.AuthActionTypes.START_TWO_FACTOR_EMAIL_CODE),
            switchMap((action: fromActions.StartTwoFactorEmailCode) =>
                this.apiTwoFactorService.startEmailCode(action.pendingToken).pipe(
                    map(response => this.buildEmailCodeSuccessAction(response)),
                    catchError(error => this.handleTwoFactorError(error)),
                ),
            ),
        ),
    );
    public resendTwoFactorEmailCode$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.AuthActionTypes.RESEND_TWO_FACTOR_EMAIL_CODE),
            switchMap((action: fromActions.ResendTwoFactorEmailCode) =>
                this.apiTwoFactorService.resendEmailCode(action.pendingToken).pipe(
                    map(response => this.buildEmailCodeSuccessAction(response)),
                    catchError(error => this.handleTwoFactorError(error)),
                ),
            ),
        ),
    );
    public verifyTwoFactorEmailCode$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.AuthActionTypes.VERIFY_TWO_FACTOR_EMAIL_CODE),
            switchMap((action: fromActions.VerifyTwoFactorEmailCode) =>
                this.apiTokenService.twoFactorToken(action.payload).pipe(
                    switchMap(token => this.completeTwoFactorLogin(token)),
                    catchError(error =>
                        this.handleTwoFactorFinalizeError(
                            action.payload,
                            error,
                            payload => new fromActions.VerifyTwoFactorEmailCode(payload),
                        ),
                    ),
                ),
            ),
        ),
    );
    public startTwoFactorIdGov$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.AuthActionTypes.START_TWO_FACTOR_ID_GOV),
            switchMap((action: fromActions.StartTwoFactorIdGov) => {
                if (isTwoFactorChallengeExpired(action.payload.expiresAt)) {
                    return of(this.buildPendingExpiredCancelAction());
                }

                this.navigateToIdGovStart(action.payload);

                return EMPTY;
            }),
        ),
    );
    public expireTwoFactorIdGovChallenge$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.AuthActionTypes.LOGIN_USER_TWO_FACTOR_REQUIRED),
            filter(
                (action: fromActions.LoginUserTwoFactorRequired) =>
                    action.payload.type === TwoFactorType.IdGov && !!action.payload.expiresAt,
            ),
            switchMap((action: fromActions.LoginUserTwoFactorRequired) =>
                timer(getTwoFactorExpiryDelayMs(action.payload.expiresAt)).pipe(
                    map(() => this.buildPendingExpiredCancelAction()),
                    takeUntil(
                        this.actions$.pipe(
                            ofType(
                                fromActions.AuthActionTypes.CANCEL_TWO_FACTOR,
                                fromActions.AuthActionTypes.LOGIN_USER_SUCCESS,
                                fromActions.AuthActionTypes.LOGOUT_USER,
                                fromActions.AuthActionTypes.LOGIN_USER,
                                fromActions.AuthActionTypes.START_TWO_FACTOR_ID_GOV,
                                fromActions.AuthActionTypes.COMPLETE_TWO_FACTOR_ID_GOV,
                            ),
                        ),
                    ),
                ),
            ),
        ),
    );
    public completeTwoFactorIdGov$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.AuthActionTypes.COMPLETE_TWO_FACTOR_ID_GOV),
            switchMap((action: fromActions.CompleteTwoFactorIdGov) =>
                this.apiTokenService.twoFactorToken(action.payload).pipe(
                    switchMap(token => this.completeTwoFactorLogin(token)),
                    catchError(error =>
                        this.handleTwoFactorFinalizeError(
                            action.payload,
                            error,
                            payload => new fromActions.CompleteTwoFactorIdGov(payload),
                        ),
                    ),
                ),
            ),
        ),
    );
    public cancelTwoFactor$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(fromActions.AuthActionTypes.CANCEL_TWO_FACTOR),
                tap(() => {
                    this.twoFactorStorageService.removeValue();
                    this.router.navigateByUrl('/auth/login');
                }),
            ),
        { dispatch: false },
    );
    public startSessionIdleMonitor$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(fromActions.AuthActionTypes.LOGIN_USER_SUCCESS),
                tap(() => {
                    if (this.shouldStartSessionIdleMonitor()) {
                        this.sessionIdleMonitorService.start();
                        return;
                    }

                    this.sessionIdleMonitorService.stop(false);
                }),
            ),
        { dispatch: false },
    );
    public sessionIdleLock$ = createEffect(() =>
        this.sessionIdleMonitorService.lock$.pipe(
            map(
                lock =>
                    new fromActions.LogoutUser({
                        reason: 'inactivity',
                        messageKey: 'sessionLifecycle.inactivityLocked',
                        messageParams: {
                            minutes: lock.minutes,
                        },
                        fromSync: lock.fromSync,
                    }),
            ),
        ),
    );
    public sessionIdleStop$ = createEffect(() =>
        this.sessionIdleMonitorService.stop$.pipe(
            map(
                () =>
                    new fromActions.LogoutUser({
                        reason: 'manual',
                        fromSync: true,
                    }),
            ),
        ),
    );
    public logoutUser$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(fromActions.AuthActionTypes.LOGOUT_USER),
                switchMap((action: fromActions.LogoutUser) => {
                    if (action.payload.reason !== 'manual') {
                        this.clearAuthState(
                            action.payload.reason === 'unauthorized' && !action.payload.fromSync,
                        );

                        return of(null);
                    }

                    if (action.payload.fromSync) {
                        this.clearAuthState(false);

                        return of(null);
                    }

                    return this.apiUserSessionService.logout().pipe(
                        catchError(() => of(false)),
                        map(() => this.clearAuthState()),
                    );
                }),
            ),
        { dispatch: false },
    );

    constructor(
        public actions$: Actions,
        public apiTokenService: ApiTokenService,
        public apiTwoFactorService: ApiTwoFactorService,
        public apiUserService: ApiUserService,
        public apiUserSessionService: ApiUserSessionService,
        public sessionIdleMonitorService: SessionIdleMonitorService,
        public tokenStorageService: TokenStorageService,
        public roleStorageService: RoleStorageService,
        public userCredentialsStorageService: UserCredentialsStorageService,
        public twoFactorStorageService: TwoFactorStorageService,
        private router: Router,
        private dialog: MatDialog,
    ) {}

    private activateTwoFactorChallenge(challenge: TwoFactorChallenge): Observable<Action> {
        this.twoFactorStorageService.setValue(challenge);

        const route =
            challenge.type === TwoFactorType.IdGov
                ? '/auth/two-factor/id-gov'
                : '/auth/two-factor/email-code';

        this.router.navigateByUrl(route);

        return of(new fromActions.LoginUserTwoFactorRequired(challenge));
    }

    private buildEmailCodeSuccessAction(
        response: TwoFactorEmailStartResponse,
    ): fromActions.StartTwoFactorEmailCodeSuccess {
        return new fromActions.StartTwoFactorEmailCodeSuccess(
            response,
            Date.now() + this.twoFactorResendCooldownMs,
        );
    }

    private completeTwoFactorLogin(token: TokenResponse): Observable<Action> {
        const challenge = this.getTwoFactorChallenge(token);

        if (challenge) {
            return this.activateTwoFactorChallenge(challenge);
        }

        return this.completeLogin(token as AuthTokenModel);
    }

    private handleTwoFactorFinalizeError(
        payload: TwoFactorFinalizePayload,
        error: unknown,
        retryActionFactory: (payload: TwoFactorFinalizePayload) => Action,
    ): Observable<Action> {
        if (!payload.terminatePreviousSession && this.isMultisessionConflict(error)) {
            return this.openMultisessionConflictDialog().pipe(
                map(confirmed => {
                    if (confirmed) {
                        return retryActionFactory({
                            ...payload,
                            terminatePreviousSession: true,
                        });
                    }

                    return new fromActions.CancelTwoFactor({
                        messageKey: 'sessionLifecycle.multisessionCancelMessage',
                    });
                }),
            );
        }

        return this.handleTwoFactorError(error);
    }

    private handleTwoFactorError(error: unknown): Observable<Action> {
        const errorCode = this.getPrimaryErrorCode(error);
        const messageKey = this.getTwoFactorErrorMessageKey(errorCode);

        if (errorCode === 'PENDING_TOKEN_EXPIRED') {
            return of(this.buildPendingExpiredCancelAction());
        }

        if (
            [
                'IDGOVUA_ERROR',
                'DECRYPT_SERVER_ERROR',
                'UNEXPECTED_ERROR',
                'SESSION_NOT_FOUND',
                'REQUEST_DATA_NOT_VALID',
                'UNKNOWN',
            ].includes(errorCode)
        ) {
            return of(
                new fromActions.TwoFactorError({ messageKey: 'auth.twoFactor.errors.technical' }),
            );
        }

        return of(new fromActions.TwoFactorError({ messageKey }));
    }

    private getTwoFactorChallenge(value: unknown): TwoFactorChallenge | null {
        const body = this.getTwoFactorResponseBody(value);
        const codes = this.getErrorCodes(body);

        if (!codes.includes('2FA_REQUIRED')) {
            return null;
        }

        const pendingToken = this.getFirstStringValue([
            body?.pendingToken,
            (value as { pendingToken?: string })?.pendingToken,
        ]);
        const type = this.normalizeTwoFactorType(
            body?.type ?? (value as { type?: string | number })?.type,
        );

        if (!pendingToken || !type) {
            return null;
        }

        return {
            type,
            pendingToken,
            maskedEmail: body?.maskedEmail || (value as { maskedEmail?: string })?.maskedEmail,
            expiresAt: Date.now() + this.getTwoFactorPendingTokenTtlMs(body, value),
        };
    }

    private getTwoFactorPendingTokenTtlMs(body: LoginErrorBody, value: unknown): number {
        const rawValue = value as LoginErrorBody;
        const expiresInSeconds = Number(
            body?.expiresIn ??
                body?.pendingTokenExpiresIn ??
                rawValue?.expiresIn ??
                rawValue?.pendingTokenExpiresIn,
        );

        if (Number.isFinite(expiresInSeconds) && expiresInSeconds > 0) {
            return expiresInSeconds * 1000;
        }

        return this.twoFactorPendingTokenTtlMs;
    }

    private buildPendingExpiredCancelAction(): fromActions.CancelTwoFactor {
        return new fromActions.CancelTwoFactor({
            messageKey: 'auth.twoFactor.errors.pendingExpired',
        });
    }

    private navigateToIdGovStart(payload: fromActions.StartTwoFactorIdGovPayload): void {
        window.location.href = this.apiTwoFactorService.getIdGovStartUrl(
            payload.pendingToken,
            payload.returnUrl,
            payload.deepLink,
        );
    }

    private normalizeTwoFactorType(
        value: string | number | null | undefined,
    ): TwoFactorType | null {
        if (Number(value) === TwoFactorType.IdGov) {
            return TwoFactorType.IdGov;
        }

        if (Number(value) === TwoFactorType.EmailCode) {
            return TwoFactorType.EmailCode;
        }

        const normalizedValue = String(value || '')
            .toLowerCase()
            .replace(/[\s_\-.]+/g, '');

        if (['idgov', 'idgovua'].includes(normalizedValue)) {
            return TwoFactorType.IdGov;
        }

        if (['emailcode', 'email'].includes(normalizedValue)) {
            return TwoFactorType.EmailCode;
        }

        return null;
    }

    private getPrimaryErrorCode(error: unknown): TwoFactorErrorCode | string {
        return this.getErrorCodes(this.getLoginErrorBody(error))[0] || 'UNKNOWN';
    }

    private getTwoFactorResponseBody(value: unknown): LoginErrorBody {
        const directBody = this.toLoginErrorBody(value);

        if (this.getErrorCodes(directBody).includes('2FA_REQUIRED')) {
            return directBody;
        }

        return this.getLoginErrorBody(value);
    }

    private getErrorCodes(body: LoginErrorBody): string[] {
        return this.flatten([
            body?.ErrorCode,
            body?.error,
            body?.error_description,
            body?.code,
            body?.Code,
            body?.reason,
            body?.Reason,
        ])
            .filter(Boolean)
            .map(String);
    }

    private getTwoFactorErrorMessageKey(errorCode: string): TwoFactorTranslationKey {
        switch (errorCode) {
            case '2FA_RNOKPP_MISSING':
                return 'auth.twoFactor.errors.incompleteUserData';
            case 'EMAIL_MISSING':
                return 'auth.twoFactor.errors.emailMissing';
            case '2FA_CODE_INVALID':
                return 'auth.twoFactor.errors.invalidCode';
            case '2FA_CODE_EXPIRED':
                return 'auth.twoFactor.errors.expiredCode';
            case '2FA_CODE_RESEND_TOO_SOON':
                return 'auth.twoFactor.errors.resendTooSoon';
            case '2FA_NOT_VALIDATED':
                return 'auth.twoFactor.errors.notValidated';
            case 'USER_TAX_MISMATCH':
                return 'auth.twoFactor.errors.taxMismatch';
            case 'USER_FULLNAME_MISMATCH':
                return 'auth.twoFactor.errors.fullNameMismatch';
            case 'PENDING_TOKEN_EXPIRED':
                return 'auth.twoFactor.errors.pendingExpired';
            default:
                return 'auth.twoFactor.errors.technical';
        }
    }

    private completeLogin(token: AuthTokenModel): Observable<Action> {
        this.twoFactorStorageService.removeValue();
        this.persistAuthState(token);

        const { user } = token;
        const roles = this.roleStorageService.getValue();
        const recipients = this.roleStorageService.getReceptionsValue();
        const isSubRec =
            roles?.includes(UserRoleEnum.AdminSubRecipient) ||
            roles?.includes(UserRoleEnum.AdminSubRecipientPlus);

        if (isSubRec) {
            if (!recipients?.length) {
                this.roleStorageService.setValueModules('');
                return of(new fromActions.LoginUserSuccess(user));
            }

            if (recipients?.length > 1) {
                const data: ChangeRecipientPopupData = {
                    recipients,
                    currentRecipient: null,
                    title: 'nav.chooseRecipient',
                    required: true,
                    label: 'nav.changeRecipientSubtitleAuth',
                };

                const dialogRef = this.dialog.open(ChangeRecipientPopupComponent, {
                    width: '800px',
                    panelClass: 'popup',
                    autoFocus: false,
                    data,
                    disableClose: true,
                });

                return dialogRef.afterClosed().pipe(
                    first(),
                    map(value => {
                        this.roleStorageService.setValueCurrentRecipient(value);

                        const modules = value?.Modules;
                        this.roleStorageService.setValueModules(modules?.join(','));
                        return new fromActions.LoginUserSuccess(user);
                    }),
                );
            }

            const modules = recipients[0]?.Modules;
            this.roleStorageService.setValueModules(modules?.join(','));

            this.roleStorageService.setValueCurrentRecipient(recipients[0]);
            return of(new fromActions.LoginUserSuccess(user));
        }

        const modules = token?.user?.Modules;
        this.roleStorageService.setValueModules(modules?.join(','));

        return of(new fromActions.LoginUserSuccess(user));
    }

    private persistAuthState(token: AuthTokenModel): void {
        const { access_token } = token;
        const roles = token?.user?.Roles;
        const recipients = token?.user?.Recipients;

        this.roleStorageService.setValue(roles);
        if (recipients?.length) {
            this.roleStorageService.setValueRecipients(recipients);
        }
        this.tokenStorageService.setValue(access_token);
        this.userCredentialsStorageService.setValue(
            `${token.user.FirstName || 'G'} ${token.user.LastName || 'G'}`,
        );
        localStorage.setItem('org', token.user.Organization?.Name);
        localStorage.setItem('orgID', token.user.Organization?.Id);
        localStorage.setItem('userID', token.user?.Id);
        localStorage.setItem('specialRights', token.user?.SpecialRights);
    }

    private openMultisessionConflictDialog(): Observable<boolean> {
        return this.dialog
            .open(MultisessionConflictDialogComponent, {
                panelClass: 'popup',
                disableClose: true,
                autoFocus: false,
            })
            .afterClosed()
            .pipe(first());
    }

    private clearAuthState(syncSessionIdleState = true): void {
        this.dialog.closeAll();
        this.sessionIdleMonitorService.stop(syncSessionIdleState);
        this.roleStorageService.removeValue();
        this.tokenStorageService.removeValue();
        this.roleStorageService.removeRecipientValue();
        this.userCredentialsStorageService.removeValue();
        localStorage.removeItem('specialRights');
        localStorage.removeItem('orgID');
        localStorage.removeItem('org');
        localStorage.removeItem('userID');
        this.router.navigateByUrl('/auth/login');
    }

    private shouldStartSessionIdleMonitor(): boolean {
        const roles = this.roleStorageService.getValue() ?? [];

        return roles.some(role => SESSION_IDLE_MONITOR_ROLES.includes(role as UserRoleEnum));
    }

    private handleLoginError(
        payload: fromActions.LoginUserPayload,
        error: unknown,
    ): Observable<Action> {
        if (!payload.terminatePreviousSession && this.isMultisessionConflict(error)) {
            return this.openMultisessionConflictDialog().pipe(
                map(confirmed => {
                    if (confirmed) {
                        return new fromActions.LoginUser({
                            ...payload,
                            terminatePreviousSession: true,
                        });
                    }

                    return new fromActions.SetAuthSessionMessage(
                        'sessionLifecycle.multisessionCancelMessage',
                    );
                }),
            );
        }

        const expiredPasswordToken = this.getExpiredPasswordToken(error);

        if (expiredPasswordToken && this.isExpiredPasswordError(error)) {
            this.router.navigate(['/set-password'], {
                queryParams: {
                    token: expiredPasswordToken,
                    mode: 'expired-password',
                },
                state: {
                    pendingUsername: payload.email,
                },
            });

            return of(new fromActions.LoginUserErrorClear(true));
        }

        return of(new fromActions.LoginUserError(error));
    }

    private isExpiredPasswordError(error: unknown): boolean {
        const errorBody = this.getLoginErrorBody(error);

        if (errorBody?.isPasswordExpired || errorBody?.IsPasswordExpired) {
            return true;
        }

        const values = this.flatten([
            errorBody?.ErrorCode,
            errorBody?.error,
            errorBody?.error_description,
            errorBody?.code,
            errorBody?.Code,
            errorBody?.message,
            errorBody?.Message,
            errorBody?.reason,
            errorBody?.Reason,
        ])
            .filter(Boolean)
            .map(String);

        if (values.some(value => this.isNecessaryPasswordResetCode(value))) {
            return true;
        }

        return values.some(value => this.isExpiredPasswordIndicator(value));
    }

    private getExpiredPasswordToken(error: unknown): string | null {
        const errorBody = this.getLoginErrorBody(error);
        const passwordSetupToken = this.getFirstStringValue([
            errorBody?.passwordSetupToken,
            errorBody?.PasswordSetupToken,
        ]);

        if (passwordSetupToken) {
            return passwordSetupToken;
        }

        return this.getFirstStringValue([
            errorBody?.token,
            errorBody?.Token,
            errorBody?.changePasswordToken,
            errorBody?.ChangePasswordToken,
            errorBody?.passwordChangeToken,
            errorBody?.PasswordChangeToken,
            errorBody?.passwordResetToken,
            errorBody?.PasswordResetToken,
            errorBody?.resetPasswordToken,
            errorBody?.ResetPasswordToken,
            errorBody?.data?.token,
            errorBody?.data?.Token,
            errorBody?.Data?.token,
            errorBody?.Data?.Token,
            errorBody?.details?.token,
            errorBody?.details?.Token,
            errorBody?.Details?.token,
            errorBody?.Details?.Token,
        ]);
    }

    private getLoginErrorBody(error: unknown): LoginErrorBody {
        const errorBody = this.getHttpErrorBody(error);

        if (typeof errorBody === 'string') {
            try {
                return this.toLoginErrorBody(JSON.parse(errorBody));
            } catch {
                return { error_description: errorBody };
            }
        }

        return this.toLoginErrorBody(errorBody);
    }

    private isExpiredPasswordIndicator(value: string): boolean {
        const normalizedValue = this.normalizeErrorText(value);

        return (
            this.expiredPasswordIndicators.some(indicator => normalizedValue.includes(indicator)) ||
            (normalizedValue.includes('password') && normalizedValue.includes('expired')) ||
            (normalizedValue.includes('парол') &&
                (normalizedValue.includes('минув') ||
                    normalizedValue.includes('истек') ||
                    normalizedValue.includes('закінчив') ||
                    normalizedValue.includes('завершив') ||
                    normalizedValue.includes('просроч')))
        );
    }

    private isNecessaryPasswordResetCode(value: string): boolean {
        return (
            value === this.necessaryPasswordResetCode ||
            this.normalizeErrorText(value) ===
                this.normalizeErrorText(this.necessaryPasswordResetCode)
        );
    }

    private normalizeErrorText(value: string): string {
        return value.toLowerCase().replace(/[\s_\-.:;,'"()]+/g, '');
    }

    private isMultisessionConflict(error: unknown): boolean {
        const errorBody = this.getLoginErrorBody(error);
        const codes = this.flatten([
            errorBody?.ErrorCode,
            errorBody?.error,
            errorBody?.error_description,
            errorBody?.code,
            errorBody?.Code,
            errorBody?.reason,
            errorBody?.Reason,
        ])
            .filter(Boolean)
            .map(String);

        const hasConflictCode = codes.some(code =>
            [
                'ActiveSessionExists',
                'MultiSessionConflict',
                'MultisessionConflict',
                'SessionAlreadyExists',
                'PreviousSessionActive',
                'ACTIVE_SESSIONS_ON_OTHER_DEVICE',
            ].includes(code),
        );

        if (!hasConflictCode) {
            return false;
        }

        const roles = this.flatten([
            errorBody?.Roles,
            errorBody?.roles,
            errorBody?.UserRoles,
            errorBody?.userRoles,
        ])
            .filter(role => role !== undefined && role !== null)
            .map(role => Number(role));

        return (
            !roles.length ||
            roles.some(role => this.multisessionRestrictedRoles.includes(role as UserRoleEnum))
        );
    }

    private getHttpErrorBody(error: unknown): unknown {
        return error && typeof error === 'object' && 'error' in error
            ? (error as { error?: unknown }).error
            : null;
    }

    private toLoginErrorBody(value: unknown): LoginErrorBody {
        return value && typeof value === 'object' ? (value as LoginErrorBody) : {};
    }

    private flatten(values: unknown[]): unknown[] {
        return values.reduce<unknown[]>(
            (acc, value) => acc.concat(Array.isArray(value) ? value : [value]),
            [],
        );
    }

    private getFirstStringValue(values: unknown[]): string | null {
        return (
            this.flatten(values).find(
                (value): value is string => typeof value === 'string' && value.length > 0,
            ) ?? null
        );
    }
}
